// Shared "how well is this load balanced?" panels used by both the auto Container
// Planner and the interactive Load Board — same visual language, same data source
// (plan.weight_distribution + plan.quality) so users can compare like-for-like.
import { AlertTriangle, CheckCircle2, TrendingDown } from "lucide-react";

// Dual-unit weight formatter — lbs LEFT (amber, imperial), kg RIGHT (sky, metric).
// Two visually distinct hues so US operators reading the scale ticket and metric
// yard staff reading the pallet slips can each spot "their" number instantly.
export const KG_TO_LB = 2.20462;
export function WeightLbsKg({ kg = 0, size = "sm", inline = true }) {
  const lbs = Math.round((Number(kg) || 0) * KG_TO_LB);
  const kgv = Math.round(Number(kg) || 0);
  const sizeCls = size === "lg" ? "text-xl font-semibold tabular-nums" : "text-[11px] font-mono tabular-nums";
  const unitCls = size === "lg" ? "text-[10px] uppercase tracking-wider opacity-80 ml-1" : "text-[9px] uppercase tracking-wider opacity-80 ml-0.5";
  const Comp = inline ? "span" : "div";
  return (
    <Comp className={`${sizeCls} whitespace-nowrap`} data-testid="weight-lbs-kg">
      <span className="text-amber-500" data-testid="weight-lbs">{lbs.toLocaleString()}<span className={unitCls}>lbs</span></span>
      <span className="mx-1.5 text-slate-400">·</span>
      <span className="text-sky-500" data-testid="weight-kg">{kgv.toLocaleString()}<span className={unitCls}>kg</span></span>
    </Comp>
  );
}

export function SplitBar({ leftPct, rightPct, ok, leftLabel, rightLabel, guideline }) {
  return (
    <div>
      <div className="relative h-6 rounded overflow-hidden bg-slate-100 flex text-[11px] font-medium text-white">
        <div className={`${ok ? "bg-emerald-500" : "bg-rose-500"} flex items-center justify-center`} style={{ width: `${leftPct}%` }}>{leftPct >= 12 ? `${leftPct}%` : ""}</div>
        <div className={`${ok ? "bg-emerald-600" : "bg-rose-600"} flex items-center justify-center`} style={{ width: `${rightPct}%` }}>{rightPct >= 12 ? `${rightPct}%` : ""}</div>
        {/* center 50% marker */}
        <div className="absolute top-0 bottom-0 w-px bg-slate-700/60" style={{ left: "50%" }} />
        {guideline && <div className="absolute top-0 bottom-0 w-px bg-amber-400" style={{ left: "60%" }} title="60% guideline" />}
      </div>
      <div className="flex justify-between text-[11px] text-slate-500 mt-0.5"><span>{leftLabel}</span><span>{rightLabel}</span></div>
    </div>
  );
}

export function QualityCard({ quality, testidPrefix = "" }) {
  if (!quality) return null;
  const g = quality.grade;
  const tone = g === "good" ? { bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Good" }
    : g === "fair" ? { bar: "bg-amber-500", chip: "bg-amber-100 text-amber-700 border-amber-200", label: "Fair" }
      : { bar: "bg-rose-500", chip: "bg-rose-100 text-rose-700 border-rose-200", label: "Poor" };
  const icon = (lvl) => lvl === "ok"
    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
    : lvl === "warn" ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
      : <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />;
  const tid = (s) => (testidPrefix ? `${testidPrefix}-${s}` : s);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={tid("load-quality")}>
      <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 bg-slate-50">
        <span className="text-sm font-semibold text-slate-700">Load quality</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone.chip}`} data-testid={tid("quality-grade")}>{tone.label}</span>
        <div className="ml-auto flex items-center gap-2 w-44">
          <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${tone.bar} transition-all`} style={{ width: `${quality.score}%` }} /></div>
          <span className="text-sm font-bold text-slate-700 tabular-nums" data-testid={tid("quality-score")}>{quality.score}<span className="text-[10px] font-normal text-slate-400">/100</span></span>
        </div>
      </div>
      {quality.best_move && (
        <div className="px-3 py-2 text-[12px] bg-blue-50 text-blue-800 flex items-start gap-1.5" data-testid={tid("quality-best-move")}>
          <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><b>Best next move:</b> {quality.best_move}</span>
        </div>
      )}
      <ul className="p-3 space-y-1.5">
        {(quality.findings || []).map((f, i) => (
          <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-600" data-testid={tid(`quality-finding-${i}`)}>
            <span className="mt-0.5 shrink-0">{icon(f.level)}</span><span>{f.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Two split bars side by side — front/rear (with FHWA 60% guideline) + left/right.
// Same visual language as the Container Planner so users can compare like-for-like.
export function WeightDistribution({ wd, testidPrefix = "" }) {
  if (!wd) return null;
  const tid = (s) => (testidPrefix ? `${testidPrefix}-${s}` : s);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={tid("weight-distribution")}>
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">Front / Rear split
          <span className={`text-[11px] px-1.5 py-0.5 rounded ${wd.fhwa_ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid={tid("fr-status")}>{wd.fhwa_ok ? "60/40 OK" : "60/40 FAIL"}</span>
        </div>
        <SplitBar leftPct={wd.front_pct} rightPct={wd.rear_pct} ok={wd.fhwa_ok}
          leftLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Front</span><WeightLbsKg kg={wd.front_kg} /></span>}
          rightLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Rear</span><WeightLbsKg kg={wd.rear_kg} /></span>} guideline />
        <p className="text-[11px] text-slate-400 mt-1.5">Longitudinal CoG at {wd.cog_long_pct}% of length (ideal ≈ 50%). Amber line = 60% FHWA guideline.</p>
      </div>
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">Left / Right balance
          <span className={`text-[11px] px-1.5 py-0.5 rounded ${wd.lr_ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={tid("lr-status")}>{wd.lr_ok ? "Balanced" : "Uneven"}</span>
        </div>
        <SplitBar leftPct={wd.left_pct} rightPct={wd.right_pct} ok={wd.lr_ok}
          leftLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Left</span><WeightLbsKg kg={wd.left_kg} /></span>}
          rightLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Right</span><WeightLbsKg kg={wd.right_kg} /></span>} />
        <p className="text-[11px] text-slate-400 mt-1.5">Even left/right keeps the trailer stable in corners.</p>
      </div>
    </div>
  );
}
